import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  HomeIcon,
  ClipboardDocumentCheckIcon,
  AcademicCapIcon,
  UserGroupIcon,
  CalendarDaysIcon,
  BuildingLibraryIcon,
  CreditCardIcon,
  ChartBarIcon,
  PresentationChartBarIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  XMarkIcon,
  AcademicCapIcon as CertificationIcon,
  TagIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { APP_LOGO, APP_LOGO_ALT, APP_SHELL_GRADIENT, APP_SHELL_BORDER } from '../../constants/branding';

const SIDEBAR_GRADIENT = APP_SHELL_GRADIENT;
const SIDEBAR_BORDER = APP_SHELL_BORDER;

const NavLink = ({ item, pathname, onNavigate }) => {
  const Icon = item.icon;
  const current =
    item.href === '/dashboard' ? pathname === '/dashboard' : pathname.startsWith(item.href);

  return (
    <Link
      to={item.href}
      onClick={onNavigate}
      className={`group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
        current
          ? 'bg-white/20 text-white shadow-sm'
          : 'text-white/90 hover:bg-white/10 hover:text-white'
      }`}
    >
      <Icon
        className={`h-5 w-5 shrink-0 ${
          current ? 'text-white' : 'text-white/70 group-hover:text-white'
        }`}
        aria-hidden
      />
      <span className="truncate">{item.name}</span>
    </Link>
  );
};

const Sidebar = ({ isMobile, isDesktopOpen, toggleMobileSidebar }) => {
  const location = useLocation();
  const { pathname } = location;
  const { t, isRTL } = useLanguage();

  // Order follows creation dependencies (prerequisites first):
  // Trainers → Classrooms → Courses → Students → Groups → Schedule → …
  const menuItems = useMemo(
    () => [
      { name: t('nav.dashboard'), href: '/dashboard', icon: HomeIcon },
      { name: t('nav.trainers'), href: '/trainers', icon: UserGroupIcon },
      { name: t('nav.classrooms'), href: '/classrooms', icon: BuildingLibraryIcon },
      { name: t('nav.courses'), href: '/courses', icon: ClipboardDocumentCheckIcon },
      { name: t('nav.students'), href: '/students', icon: AcademicCapIcon },
      { name: t('nav.groups'), href: '/groups', icon: TagIcon },
      { name: t('nav.schedule'), href: '/schedule', icon: CalendarDaysIcon },
      { name: t('nav.evaluations'), href: '/evaluations', icon: ClipboardDocumentListIcon },
      { name: t('nav.certifications'), href: '/certifications', icon: CertificationIcon },
      { name: t('nav.payments'), href: '/payments', icon: CreditCardIcon },
      { name: t('nav.benefits'), href: '/benefits', icon: ChartBarIcon },
      { name: t('nav.reports'), href: '/reports', icon: PresentationChartBarIcon },
      { name: t('nav.settings'), href: '/settings', icon: Cog6ToothIcon },
    ],
    [t]
  );

  return (
    <>
      {/* Desktop */}
      <motion.div
        initial={{ width: isDesktopOpen ? 280 : 0 }}
        animate={{
          width: isDesktopOpen ? 280 : 0,
          opacity: isDesktopOpen ? 1 : 0,
        }}
        className={`fixed bottom-0 top-0 z-20 hidden overflow-hidden ${SIDEBAR_BORDER} lg:block ltr:left-0 ltr:border-r rtl:right-0 rtl:border-l`}
        aria-hidden={!isDesktopOpen}
      >
        <div className="flex h-full w-[280px] flex-col">
          <div className="flex h-14 shrink-0 items-center justify-center border-b border-slate-200/90 bg-white px-4 dark:border-slate-800 dark:bg-slate-950 sm:h-16">
            <Link
              to="/dashboard"
              className="flex h-full w-full items-center justify-center"
            >
              <img
                src={APP_LOGO}
                alt={APP_LOGO_ALT}
                className="max-h-8 w-auto max-w-[220px] object-contain sm:max-h-9"
              />
            </Link>
          </div>
          <div className={`flex min-h-0 flex-1 flex-col ${SIDEBAR_GRADIENT}`}>
            <nav className="hide-scrollbar flex-1 space-y-1 overflow-y-auto px-3 py-3 pb-4">
              {menuItems.map((item) => (
                <NavLink key={item.href} item={item} pathname={pathname} />
              ))}
            </nav>
            <div className="border-t border-white/15 p-3">
            <Link
              to="/login"
              className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-white/90 transition hover:bg-white/10 hover:text-white"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5 shrink-0 text-white/80" aria-hidden />
              {t('layout.logout')}
            </Link>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Mobile */}
      <motion.div
        initial={{ x: isRTL ? '100%' : '-100%' }}
        animate={{ x: isMobile ? '0%' : isRTL ? '100%' : '-100%' }}
        transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
        className={`fixed inset-y-0 z-50 w-[280px] ${SIDEBAR_BORDER} shadow-xl lg:hidden ltr:left-0 ltr:border-r rtl:right-0 rtl:border-l`}
        role="dialog"
        aria-modal="true"
        aria-label={t('layout.mainNavAria')}
      >
        <div className={`flex h-full flex-col ${SIDEBAR_GRADIENT}`}>
          <div className="flex h-14 shrink-0 items-center justify-between border-b border-white/15 bg-white px-4 dark:bg-slate-950 sm:h-16">
            <Link
              to="/dashboard"
              onClick={toggleMobileSidebar}
              className="flex min-w-0 flex-1 items-center justify-center rounded-lg bg-white px-3 py-1.5"
            >
              <img src={APP_LOGO} alt={APP_LOGO_ALT} className="h-8 w-auto max-w-[180px] object-contain" />
            </Link>
            <button
              type="button"
              onClick={toggleMobileSidebar}
              className="rounded-lg p-2 text-forma-700 hover:bg-forma-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-forma-500/40"
            >
              <span className="sr-only">{t('layout.menuCloseSr')}</span>
              <XMarkIcon className="h-6 w-6" aria-hidden />
            </button>
          </div>
          <nav className="hide-scrollbar flex-1 space-y-1 overflow-y-auto p-3">
            {menuItems.map((item) => (
              <NavLink
                key={item.href}
                item={item}
                pathname={pathname}
                onNavigate={toggleMobileSidebar}
              />
            ))}
          </nav>
          <div className="border-t border-white/15 p-3">
            <Link
              to="/login"
              onClick={toggleMobileSidebar}
              className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-white/90 transition hover:bg-white/10 hover:text-white"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5 shrink-0 text-white/80" aria-hidden />
              {t('layout.logout')}
            </Link>
          </div>
        </div>
      </motion.div>

      {isMobile ? (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-[1px] lg:hidden"
          aria-label={t('layout.overlayCloseNav')}
          onClick={toggleMobileSidebar}
        />
      ) : null}
    </>
  );
};

export default Sidebar;
